import React, { useState, useEffect, useRef, useCallback } from 'react';
import { StepHelpers } from '@shared/components/Stepper';
import { Button } from '@shared/ui/button';
import { toast } from '@shared/hooks/use-toast';
import { PageHeader } from '@shared/components/PageHeader';
import { Check } from 'lucide-react';
import { useRateTypes } from '../../hooks';
import { useNavigate } from 'react-router-dom';
import { DiscardDialog } from './DiscardDialog';

interface RateStructureProps {
  remoteUtilityId: number;
  storageKey: string;
  logTimeUntilStep?: (step: number | string) => void;
  stepHelpers?: StepHelpers;
  currentStepIndex?: number;
  onNext?: () => void;
  onPrevious?: () => void;
  setValidationError?: (error: string) => void;
  clearValidationError?: () => void;
  isValidating?: boolean;
}

export const RateStructure: React.FC<RateStructureProps> = ({
  remoteUtilityId,
  storageKey,
  logTimeUntilStep,
  stepHelpers,
  currentStepIndex = 0,
  onNext,
  onPrevious,
  setValidationError,
  clearValidationError,
  isValidating
}) => {
  const dataLoadedRef = useRef(false);
  const [formData, setFormData] = useState<any>({});
  const [selectedType, setSelectedType] = useState<number | null>(null);
  const navigate = useNavigate();
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);

  const handleDiscard = () => {
    if (stepHelpers && stepHelpers.resetAllData) {
      stepHelpers.resetAllData();
    } else {
      localStorage.removeItem(storageKey);
    }
    navigate("/plan-tarrif/plan-tarrif-landing?tab=tariff");
  };

  const saveStepData = useCallback(
    (data: any) => {
      if (!stepHelpers) return;

      try {
        const stepData = {
          ...data,
          timestamp: new Date().toISOString(),
          stepIndex: currentStepIndex,
        };

        stepHelpers.setStepData(currentStepIndex, stepData);
      } catch (error) {
        console.error("Failed to save step data via stepHelpers:", error);
        toast({
          title: "Failed to save progress",
          description: "Your progress may not be saved. Please try again.",
          variant: "destructive",
        });
      }
    },
    [stepHelpers, currentStepIndex]
  );

  const getStepData = useCallback(() => {
    if (!stepHelpers) return null;

    try {
      return stepHelpers.getStepData(currentStepIndex);
    } catch (error) {
      console.error("Failed to get step data via stepHelpers:", error);
      return null;
    }
  }, [stepHelpers, currentStepIndex]);

  useEffect(() => {
    if (stepHelpers && !dataLoadedRef.current) {
      try {
        const savedStepData = getStepData();
        if (savedStepData) {
          setFormData(savedStepData);
          if (savedStepData.rateType) {
            setSelectedType(savedStepData.rateType);
          }
        }
        dataLoadedRef.current = true;
      } catch (error) {
        console.error("Failed to load step data:", error);
        dataLoadedRef.current = true;
      }
    }
  }, [stepHelpers, currentStepIndex, getStepData]);

  const handleContinue = () => {
    if (clearValidationError) {
      clearValidationError();
    }

    const selectedItem = rateTypes.find(t => t.key === selectedType);

    saveStepData({
      ...formData,
      rateType: selectedType,
      rateTypeName: selectedItem?.value || '',
      isStepComplete: true,
      completedAt: new Date().toISOString(),
    });

    if (logTimeUntilStep) {
      logTimeUntilStep("Rate Structure");
    }

    if (onNext) onNext();
  };
  
  const {data:rateTypes}=useRateTypes();

  return (
    <div className="space-y-6">
      <PageHeader title='Rate Structure' subtitle='Define the tier and billing structure.'/>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
      {rateTypes?.map((item) => {
        const isSelected = item.key === selectedType;

        return (
          <button
            key={item.key}
            onClick={() => setSelectedType(item.key)}
            className={`
              group relative flex flex-col items-start justify-between
              rounded-2xl border p-6 text-left transition-all duration-200
              hover:shadow-md
              ${
                isSelected
                  ? "border-blue-500 bg-blue-50/50 shadow-sm ring-1 ring-blue-500"
                  : "border-gray-200 bg-white hover:border-gray-300"
              }
            `}
          >
            <div className="flex w-full items-start justify-between gap-4">
              <div className="flex-1">
                <h3 className={`text-lg font-semibold ${isSelected ? 'text-blue-900' : 'text-gray-900'}`}>
                  {item.value}
                </h3>
                <p className="mt-2 text-sm text-gray-500 leading-relaxed">
                  {item.description ?? 'NA'}
                </p>
              </div>

              <div className={`
                flex h-6 w-6 shrink-0 items-center justify-center rounded-full border transition-colors
                ${isSelected 
                  ? 'border-blue-500 bg-blue-500' 
                  : 'border-gray-300 bg-white group-hover:border-gray-400'
                }
              `}>
                {isSelected && <Check className="h-4 w-4 text-white" strokeWidth={3} />}
              </div>
            </div>
          </button>
        );
      })}
    </div>

      <div className="flex justify-between pt-4">
        <Button
          variant="outline"
          onClick={onPrevious}
          disabled={isValidating}
        >
          Previous
        </Button>
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={() => setShowDiscardDialog(true)}
          >
            Cancel
          </Button>
          <Button
            onClick={handleContinue}
            disabled={isValidating || !selectedType}
            className="min-w-[120px]"
          >
            Continue
          </Button>
        </div>
      </div>
      <DiscardDialog open={showDiscardDialog} onOpenChange={setShowDiscardDialog} onDiscard={handleDiscard} />
    </div>
  );
};
